/**
 * billingGrant.ts — the words for a Pro that is on us (2026-09-16).
 *
 * /api/billing/status carries `grant` when the account's Pro comes from a
 * grant rather than a store subscription (Worker: lib/proGrantDescription).
 * The Billing page prints it as "Pro · On us", why, and until when, and
 * hides the store-management buttons that have nothing to manage.
 */

export interface BillingGrant {
  source: "android_beta" | "redeemer_verified" | "inviter_milestone" | "referral" | "admin" | "gift";
  /** ms epoch the Pro lapses, or null for an open-ended grant. */
  expiresAt: number | null;
  days: number | null;
  grantedAt: number | null;
}

/** Why the Pro is on us, in the trainer's words. */
export function grantSourceLabel(source: BillingGrant["source"]): string {
  switch (source) {
    case "android_beta":
      return "Android beta thank-you";
    case "redeemer_verified":
      return "Friend invite";
    case "inviter_milestone":
      return "Friends you brought in";
    case "referral":
      return "Friend referral";
    case "admin":
    case "gift":
    default:
      return "A gift from Nexus Dex";
  }
}

/** "Free until 15 December 2026", or "Free, no end date". */
export function grantUntilLabel(grant: BillingGrant, now = Date.now()): string {
  if (grant.expiresAt == null) return "Free, no end date";
  const d = new Date(grant.expiresAt);
  if (!Number.isFinite(d.getTime())) return "Free, no end date";
  const date = d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const daysLeft = Math.ceil((grant.expiresAt - now) / (24 * 3600 * 1000));
  return daysLeft > 0 ? `Free until ${date} (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)` : `Free until ${date}`;
}

/** Read the grant off the status payload, tolerating older Workers. */
export function billingGrantOf(status: unknown): BillingGrant | null {
  const g = (status as { grant?: unknown } | null)?.grant;
  if (!g || typeof g !== "object") return null;
  const source = (g as { source?: unknown }).source;
  if (typeof source !== "string") return null;
  const expiresAt = (g as { expiresAt?: unknown }).expiresAt;
  return {
    source: source as BillingGrant["source"],
    expiresAt: typeof expiresAt === "number" ? expiresAt : null,
    days: typeof (g as { days?: unknown }).days === "number" ? ((g as { days: number }).days) : null,
    grantedAt: typeof (g as { grantedAt?: unknown }).grantedAt === "number" ? ((g as { grantedAt: number }).grantedAt) : null,
  };
}
